import React from "react";
import { v4 as uuid } from "uuid";
import { useQuery } from "@apollo/client";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Table from "react-bootstrap/Table";
import { toast } from "react-toastify";
import FormControl from "react-bootstrap/FormControl";
import { useAuth } from "state/auth";
import { camelCaseToTitle } from "../../theme/helpers/utils";
import { useModalContext } from "state/modal";
import { gettext } from "i18n";
import {
  MetaDataField,
  PickerTemplate,
  WIDGET_TEMPLATES,
  DASHBOARD_TYPE_REGULAR,
  DASHBOARD_TYPE_REPORTING,
} from "utils/widgets";
import { sortByTranslatedKey } from "utils/arrays";
import { GET_DATASOURCES_META_BY_UUID } from "utils/stations";
import { mergeEditedWidget } from "utils/dashboards";
import {
  GetDatasourcesMetaByUUID,
  GetDatasourcesMetaByUUIDVariables,
} from "utils/__generated__/GetDatasourcesMetaByUUID";

interface WidgetFormModalProps {
  stationIdentifier: string;
  editedStructure: any[] | null;
  setEditedStructure: React.Dispatch<React.SetStateAction<any[] | null>>;
  setChangesMade: React.Dispatch<React.SetStateAction<boolean>>;
  editingWidget?: {
    widget: any;
    rowIndex: number;
    widgetIndex: number;
  };
  dashboardType?:
    | typeof DASHBOARD_TYPE_REGULAR
    | typeof DASHBOARD_TYPE_REPORTING;
}

const WidgetFormModal = ({
  setChangesMade,
  editedStructure,
  stationIdentifier,
  setEditedStructure,
  editingWidget,
  dashboardType = DASHBOARD_TYPE_REGULAR,
}: WidgetFormModalProps) => {
  const { setActiveModal } = useModalContext();
  const onHide = () => setActiveModal(null);
  const { user } = useAuth();
  const isUserAdmin = user?.admin;
  const isEditMode = !!editingWidget;

  const allSourceUUIDs = React.useMemo(() => {
    if (!editedStructure) return [];
    const uuids: string[] = [];
    editedStructure.forEach((station: any) => {
      station.station?.widget_rows?.forEach((row: any) => {
        row.widgets?.forEach((widget: any) => {
          const metaData = widget.metaData || {};
          if (metaData.sourceUUID) {
            uuids.push(metaData.sourceUUID);
          }
          if (metaData.sourceUUIDs && Array.isArray(metaData.sourceUUIDs)) {
            uuids.push(...metaData.sourceUUIDs);
          }
        });
      });
    });
    return [...new Set(uuids)];
  }, [editedStructure]);

  const { data: datasourcesData } = useQuery<
    GetDatasourcesMetaByUUID,
    GetDatasourcesMetaByUUIDVariables
  >(GET_DATASOURCES_META_BY_UUID, {
    variables: {
      uuids: `{${allSourceUUIDs.join(",")}}`,
    },
    skip: allSourceUUIDs.length === 0,
  });

  const datasourceMap = React.useMemo(() => {
    const map = new Map<string, any>();
    datasourcesData?.source?.forEach((datasource) => {
      if (datasource.source_uuid) {
        map.set(datasource.source_uuid, datasource);
      }
    });
    return map;
  }, [datasourcesData]);

  const getInitialValues = React.useCallback(() => {
    if (isEditMode) {
      const { widget } = editingWidget;
      const metaData = { ...(widget.metaData || {}) };

      if (metaData.sourceUUIDs && Array.isArray(metaData.sourceUUIDs)) {
        // Rebuild the per-station groups so the multi-station picker can
        // display sources of each station in its own row
        const stationIds: string[] = [];
        const stationSourceMap: Record<string, string[]> = {};
        metaData.sourceUUIDs.forEach((uuid: string) => {
          const datasource = datasourceMap.get(uuid);
          if (datasource?.station?.id != null) {
            const stationId = String(datasource.station.id);
            if (!stationSourceMap[stationId]) {
              stationSourceMap[stationId] = [];
              stationIds.push(stationId);
            }
            stationSourceMap[stationId].push(uuid);
          }
        });
        if (stationIds.length > 0) {
          metaData.selectedStationIds = stationIds;
          metaData.stationSourceMap = stationSourceMap;
        }
      } else if (metaData.sourceUUID) {
        const datasource = datasourceMap.get(metaData.sourceUUID);
        if (datasource?.station?.id) {
          metaData.selectedStationIds = [datasource.station.id];
        }
      }

      return {
        widgetTemplateKey: widget.key || "",
        metaData,
      };
    }
    return {
      widgetTemplateKey: "",
      metaData: {},
    };
  }, [isEditMode, editingWidget, datasourceMap]);

  const [editedValues, setEditedValues] =
    React.useState<Record<string, any>>(getInitialValues());

  React.useEffect(() => {
    if (isEditMode && datasourceMap.size > 0) {
      setEditedValues(getInitialValues());
    }
  }, [datasourceMap, isEditMode, getInitialValues]);
  const [modalDisableSave, setModalDisableSave] =
    React.useState<boolean>(false);
  const { widgetTemplateKey: selectedKey } = editedValues;

  const resetEditedValues = () => {
    setEditedValues(getInitialValues());
  };

  const closeModal = () => {
    resetEditedValues();
    setModalDisableSave(true);
    onHide();
  };

  const selectedTemplateData = WIDGET_TEMPLATES.find(
    (widgetTemplate) => widgetTemplate.key === selectedKey,
  );
  const showWidgetParams = !!Object.keys(selectedTemplateData?.metaData || {})
    .length;
  const widgetPickerList: any[] = (selectedTemplateData?.pickers ??
    []) as any[];
  const requiredFields: string[] = (selectedTemplateData?.requiredFields ??
    []) as any[];
  const showWidgetPickers = !!widgetPickerList.length;
  const showWidgetParamsOrPickers = showWidgetParams || showWidgetPickers;

  const missingRequiredValues = requiredFields?.filter(
    (field: string) => !editedValues.metaData[field],
  );

  const saveEditedStructure = async () => {
    if (missingRequiredValues?.length) {
      const missingFieldDisplay = missingRequiredValues
        .map((field) => `"${gettext(camelCaseToTitle(field))}"`)
        .join(", ");
      toast.error(
        gettext("Please fill in the required fields: ") + missingFieldDisplay,
      );
      return;
    }
    if (!selectedKey) {
      toast.error(gettext("Please select a valid widget template"));
      return;
    }

    const templateData: any = WIDGET_TEMPLATES.find(
      (widgetTemplate) => widgetTemplate.key === selectedKey,
    );
    if (!templateData) {
      toast.error(gettext("Please select a valid widget template"));
      return;
    }
    const { metaDataParsers = {} } = templateData;
    const parsedMetaData = Object.entries(editedValues.metaData).reduce(
      (acc, [key, value]) => {
        const parser: (raw: string | string[]) => any = metaDataParsers[key];
        return {
          ...acc,
          [key]: parser ? parser(value as any) : value,
        };
      },
      {},
    );

    const newEditedStructure = [...(editedStructure ?? [])];
    const stationIndex = newEditedStructure.findIndex(
      (station) => station.station.identifier === stationIdentifier,
    );
    if (stationIndex === -1) {
      toast.error(
        gettext("Something went wrong, please refresh the page and try again"),
      );
      return;
    }

    const station = newEditedStructure[stationIndex];
    const newWidgetRows = [...(station.station.widget_rows ?? [])];

    if (isEditMode) {
      const { rowIndex, widgetIndex } = editingWidget;
      const targetRow = newWidgetRows[rowIndex];
      if (!targetRow) {
        toast.error(
          gettext(
            "Something went wrong, please refresh the page and try again",
          ),
        );
        return;
      }
      const updatedWidgets = [...targetRow.widgets];
      updatedWidgets[widgetIndex] = mergeEditedWidget(
        targetRow.widgets[widgetIndex],
        selectedKey,
        {
          ...editedValues.metaData,
          widgetId: editedValues.metaData.widgetId,
          ...parsedMetaData,
        },
      );
      newWidgetRows[rowIndex] = {
        ...targetRow,
        widgets: updatedWidgets,
      };
    } else {
      const widgetId = uuid();
      const newWidget = {
        key: selectedKey,
        metaData: {
          widgetId: widgetId,
          ...parsedMetaData,
        },
      };
      newWidgetRows.push({
        widgets: [newWidget],
      });
    }

    const newStation = {
      ...station,
      station: {
        ...station.station,
        widget_rows: newWidgetRows,
      },
    };
    newEditedStructure[stationIndex] = newStation;
    setEditedStructure(newEditedStructure);
    setChangesMade(true);
    closeModal();
  };

  return (
    <Modal
      show
      onHide={closeModal}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          {isEditMode ? gettext("Edit Widget") : gettext("Add Widget")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Table responsive>
          <tbody>
            <tr>
              <th>Widget Type</th>
              <td>
                <Form.Select
                  aria-label="Widget Type"
                  onChange={(e: any) => {
                    if (!isEditMode) {
                      resetEditedValues();
                    }
                    const newTemplateKey = e.target.value;
                    const newTemplateData = (WIDGET_TEMPLATES.find(
                      (widgetTemplate) => widgetTemplate.key === newTemplateKey,
                    ) ?? {}) as any;

                    setEditedValues({
                      ...editedValues,
                      widgetTemplateKey: newTemplateKey,
                      metaData: isEditMode
                        ? editedValues.metaData
                        : {
                            ...newTemplateData.metaData,
                          },
                    });
                  }}
                  value={editedValues.widgetTemplateKey ?? ""}
                  disabled={isEditMode}
                >
                  <option value="">Select widget type</option>
                  {sortByTranslatedKey(WIDGET_TEMPLATES, "name")
                    .filter(
                      (widgetTemplate) =>
                        (isUserAdmin || !widgetTemplate.adminOnly) &&
                        (
                          widgetTemplate.availableFor ?? [
                            DASHBOARD_TYPE_REGULAR,
                            DASHBOARD_TYPE_REPORTING,
                          ]
                        ).includes(dashboardType),
                    )
                    .map((widgetTemplate) => (
                      <option
                        key={`widget_template_${widgetTemplate.key}`}
                        value={String(widgetTemplate.key)}
                      >
                        {gettext(String(widgetTemplate.name))}
                      </option>
                    ))}
                </Form.Select>
              </td>
            </tr>
            {showWidgetParamsOrPickers && (
              <tr>
                <th colSpan={2}>Widget parameters:</th>
              </tr>
            )}
            {showWidgetParams && (
              <>
                {Object.entries(selectedTemplateData?.metaData || {}).map(
                  ([key, value]) => {
                    if (key === "widgetId" && isEditMode) {
                      return null;
                    }
                    if (key === "dateRange") {
                      return null;
                    }
                    // Skip fields that have pickers - they're rendered separately below
                    const hasPicker = widgetPickerList.some(
                      (picker) => picker.field === key,
                    );
                    if (hasPicker) {
                      return null;
                    }
                    const isRequired = requiredFields?.includes(key) ?? false;
                    const placeholder = isRequired
                      ? gettext("Required")
                      : gettext("(optional)");
                    const isCheckbox = value === false || value === true;
                    if (isCheckbox) {
                      return (
                        <tr key={`widget_template_metadata_${key}`}>
                          <th>{gettext(camelCaseToTitle(key))}</th>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={!!editedValues.metaData[key]}
                              onChange={(e: any) => {
                                const value = e.target.checked;
                                setEditedValues({
                                  ...editedValues,
                                  metaData: {
                                    ...editedValues.metaData,
                                    [key]: value,
                                  },
                                });
                              }}
                            />
                          </td>
                        </tr>
                      );
                    }

                    const fieldConfig: MetaDataField | undefined =
                      selectedTemplateData?.metaDataFields?.[key];
                    const fieldType = fieldConfig?.type ?? "text";

                    if (fieldType === "color") {
                      return (
                        <tr key={`widget_template_metadata_${key}`}>
                          <th>{gettext(camelCaseToTitle(key))}</th>
                          <td>
                            <Form.Control
                              type="color"
                              value={editedValues.metaData[key] ?? value}
                              onChange={(e: any) => {
                                setEditedValues({
                                  ...editedValues,
                                  metaData: {
                                    ...editedValues.metaData,
                                    [key]: e.target.value,
                                  },
                                });
                              }}
                            />
                          </td>
                        </tr>
                      );
                    }

                    if (fieldType === "number") {
                      return (
                        <tr key={`widget_template_metadata_${key}`}>
                          <th>{gettext(camelCaseToTitle(key))}</th>
                          <td>
                            <Form.Control
                              type="number"
                              min={fieldConfig?.min}
                              max={fieldConfig?.max}
                              value={editedValues.metaData[key] ?? value}
                              placeholder={String(value || placeholder)}
                              onChange={(e: any) => {
                                setEditedValues({
                                  ...editedValues,
                                  metaData: {
                                    ...editedValues.metaData,
                                    [key]: e.target.value,
                                  },
                                });
                              }}
                            />
                          </td>
                        </tr>
                      );
                    }

                    if (fieldType === "textarea") {
                      return (
                        <tr key={`widget_template_metadata_${key}`}>
                          <th>{gettext(camelCaseToTitle(key))}</th>
                          <td>
                            <Form.Control
                              as="textarea"
                              rows={fieldConfig?.rows ?? 3}
                              value={editedValues.metaData[key] ?? ""}
                              placeholder={value || placeholder}
                              onChange={(e: any) => {
                                setEditedValues({
                                  ...editedValues,
                                  metaData: {
                                    ...editedValues.metaData,
                                    [key]: e.target.value,
                                  },
                                });
                              }}
                            />
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <tr key={`widget_template_metadata_${key}`}>
                        <th>{gettext(camelCaseToTitle(key))}</th>
                        <td>
                          <FormControl
                            type="text"
                            required={requiredFields?.includes(key) ?? false}
                            value={editedValues.metaData[key] ?? ""}
                            placeholder={value || placeholder}
                            onChange={(e: any) => {
                              const value = e.target.value;
                              setEditedValues({
                                ...editedValues,
                                metaData: {
                                  ...editedValues.metaData,
                                  [key]: value,
                                },
                              });
                            }}
                          />
                        </td>
                      </tr>
                    );
                  },
                )}
              </>
            )}
            {showWidgetPickers && (
              <>
                {widgetPickerList.map((picker: Record<string, any>) => {
                  const PickerInstance = (
                    <PickerTemplate
                      pickerTemplate={picker.key}
                      onChange={(e: any) => {
                        const value = e.target.value;
                        setEditedValues({
                          ...editedValues,
                          metaData: {
                            ...editedValues.metaData,
                            [picker.field]: value,
                          },
                        });
                      }}
                      filters={picker.filters}
                      field={picker.field}
                      params={picker.params}
                      value={editedValues?.metaData?.[picker.field] ?? ""}
                      editedValues={editedValues}
                      setEditedValues={setEditedValues}
                    />
                  );
                  return (
                    <React.Fragment
                      key={`widget_template_picker_${picker.key}`}
                    >
                      {picker.label ? (
                        <tr>
                          <th>{gettext(picker.label)}</th>
                          <td>{PickerInstance}</td>
                        </tr>
                      ) : (
                        PickerInstance
                      )}
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onHide}>Close</Button>
        <Button
          type="button"
          onClick={saveEditedStructure}
          disabled={modalDisableSave}
        >
          {isEditMode ? gettext("Save") : gettext("Add")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WidgetFormModal;
